// src/utils/offlineBookingQueue.js
//
// Offline booking queue (bookaroom-only, purely client-side).
//
// When a user submits a booking while offline (or the booking request fails
// with a network-type error), we stash the booking payload in localStorage and
// replay it automatically once connectivity returns. This is deliberately
// CONSERVATIVE and HONEST: bookaroom is a real-time booking system, so a queued
// booking is PROVISIONAL — the slot is never reserved until the replay actually
// succeeds. On replay a slot may already be taken, in which case we drop the
// queued item and report it as "could not be booked" rather than pretending it
// worked.
//
// This module never touches the backend directly and never imports any other
// app's code. `replayQueue` is handed the booking function by the caller
// (api.bookRoom), so the existing booking API is reused unchanged.
//
// Storage shape (localStorage key STORAGE_KEY): JSON array of items, each:
//   { id: string, booking: object, createdAt: number }

const STORAGE_KEY = 'bookaroom.offlineBookingQueue.v1';

// ─── Internal storage helpers (robust to JSON / quota / disabled storage) ───

// Read the raw queue array. Any failure (storage disabled, malformed JSON,
// non-array contents) is swallowed and treated as an empty queue so a corrupt
// entry can never break the UI.
function readQueue() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Keep only structurally-sane items so a partially-corrupt array still works.
    return parsed.filter(
      (item) => item && typeof item.id === 'string' && item.booking
    );
  } catch (err) {
    return [];
  }
}

// Persist the queue array. Returns true on success, false on any failure
// (e.g. QuotaExceededError, storage disabled). Callers decide how to react.
function writeQueue(queue) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    return true;
  } catch (err) {
    return false;
  }
}

// Best-effort unique id without external deps. crypto.randomUUID when available,
// otherwise a timestamp + random fallback (collision risk is negligible for the
// handful of items a single user can queue while offline).
function generateId() {
  try {
    if (
      typeof window !== 'undefined' &&
      window.crypto &&
      typeof window.crypto.randomUUID === 'function'
    ) {
      return window.crypto.randomUUID();
    }
  } catch (err) {
    // fall through to fallback
  }
  return `obq_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

// ─── Public queue API ───────────────────────────────────────────────────────

/**
 * Add a booking payload to the offline queue.
 * @param {object} booking - the exact payload that would be passed to the
 *   booking API (e.g. api.bookRoom).
 * @returns {{ id: string, booking: object, createdAt: number } | null}
 *   the stored item, or null if persistence failed (quota / disabled storage).
 */
export function enqueue(booking) {
  if (!booking || typeof booking !== 'object') return null;
  const item = {
    id: generateId(),
    booking,
    createdAt: Date.now(),
  };
  const queue = readQueue();
  queue.push(item);
  const ok = writeQueue(queue);
  return ok ? item : null;
}

/**
 * @returns {Array<{ id: string, booking: object, createdAt: number }>}
 *   a shallow copy of the queued items (oldest first).
 */
export function list() {
  return readQueue();
}

/**
 * Remove a single item by id.
 * @param {string} id
 * @returns {boolean} true if the queue was rewritten successfully.
 */
export function remove(id) {
  if (!id) return false;
  const queue = readQueue();
  const next = queue.filter((item) => item.id !== id);
  // Nothing matched — nothing to do, but report success (idempotent remove).
  if (next.length === queue.length) return true;
  return writeQueue(next);
}

/**
 * Empty the queue entirely.
 * @returns {boolean} true on success.
 */
export function clear() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (err) {
    return false;
  }
}

/**
 * @returns {number} how many bookings are currently queued.
 */
export function count() {
  return readQueue().length;
}

// ─── Replay ──────────────────────────────────────────────────────────────────

// Module-level re-entrancy guard. A single replay runs at a time across the
// whole app, even if two 'online' events arrive back-to-back. This is the key
// dedup/serialization guarantee that prevents double-submission of a queued
// booking.
let replayInFlight = false;

// Decide whether a failed replay attempt should be RETRIED later (kept in the
// queue) or treated as a PERMANENT failure (removed + reported).
//
//   - No status on the error  → network-type failure (fetch threw). The
//     request() wrapper throws a bare Error (e.g. "Failed to fetch") with no
//     `.status` in this case. Keep it for the next attempt.
//   - status >= 500           → server error. Transient; keep and retry.
//   - status 408 / 429        → timeout / rate-limit. Transient; keep and retry.
//   - any other 4xx (incl. 409 conflict) → the slot is unavailable or the
//     request is rejected for good. Drop it and report as failed.
function isRetryableError(err) {
  const status = err && typeof err.status === 'number' ? err.status : undefined;
  if (status === undefined) return true; // network failure → retry later
  if (status >= 500) return true; // server error → retry later
  if (status === 408 || status === 429) return true; // timeout / rate limited
  return false; // 4xx (incl. 409 conflict) → permanent, drop
}

/**
 * Replay every queued booking, sequentially, against the provided booking
 * function. Designed to be called when connectivity returns.
 *
 * Processing rules per item:
 *   - success                       → remove from queue, count as submitted
 *   - 409 / conflict / other 4xx    → remove from queue, count as failed
 *                                     (slot unavailable / rejected)
 *   - network error / 5xx / 408/429 → KEEP in queue for the next attempt
 *
 * Serialized + guarded so it can never double-submit: a second concurrent call
 * returns a no-op result while one is already running.
 *
 * @param {(booking: object) => Promise<any>} apiBookFn - the booking function,
 *   e.g. api.bookRoom. Called as apiBookFn(item.booking).
 * @param {object} [handlers] - optional progress callbacks:
 *   @param {(item) => void} [handlers.onItemSuccess]
 *   @param {(item, error) => void} [handlers.onItemFailed]  // permanent failure
 *   @param {(item, error) => void} [handlers.onItemRetained] // kept for retry
 * @returns {Promise<{ submitted: number, failed: number, retained: number,
 *   skipped: boolean }>} summary. `skipped: true` means a replay was already
 *   running and this call did nothing.
 */
export async function replayQueue(apiBookFn, handlers = {}) {
  const summary = { submitted: 0, failed: 0, retained: 0, skipped: false };

  if (typeof apiBookFn !== 'function') {
    return summary;
  }

  // Re-entrancy guard: never run two replays at once (prevents double-submit).
  if (replayInFlight) {
    summary.skipped = true;
    return summary;
  }
  replayInFlight = true;

  try {
    // Snapshot the ids up front and process sequentially. Re-reading the item
    // by id each step keeps us consistent if storage changed underneath us.
    const items = readQueue();
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      try {
        await apiBookFn(item.booking);
        remove(item.id);
        summary.submitted += 1;
        if (typeof handlers.onItemSuccess === 'function') {
          handlers.onItemSuccess(item);
        }
      } catch (err) {
        if (isRetryableError(err)) {
          // Transient — leave it queued and stop trying for now. If the
          // network dropped again, later items would just fail too; bail out
          // so we don't hammer a dead connection.
          summary.retained += 1;
          if (typeof handlers.onItemRetained === 'function') {
            handlers.onItemRetained(item, err);
          }
          const status = err && err.status;
          if (status === undefined) {
            // Hard network failure: abandon the rest of this pass.
            break;
          }
        } else {
          // Permanent failure (slot unavailable / rejected). Drop + report.
          remove(item.id);
          summary.failed += 1;
          if (typeof handlers.onItemFailed === 'function') {
            handlers.onItemFailed(item, err);
          }
        }
      }
    }
  } finally {
    replayInFlight = false;
  }

  return summary;
}

const offlineBookingQueue = {
  enqueue,
  list,
  remove,
  clear,
  count,
  replayQueue,
};

export default offlineBookingQueue;
